import { h, Component } from 'preact';
import { observer } from 'mobx-preact';
import { Text, Localizer } from 'preact-i18n';

// "Mine standplasser" on the Mitt stevne tab: one card per start the logged-in
// user has, with the per-stand breakdown (same data as the shooterDetail
// drawer) and a PRIVATE analysis note per start (auto-saved, 750ms delay).
@observer
class ConventionMyAnalysis extends Component {
    constructor(props) {
        super(props);
        this.state = {
            noteDrafts: {}, // stageMd5 → text while editing
            saving: {}, // stageMd5 → true while a save is in flight
        };
        this.noteTimers = {};
    }

    componentDidMount() {
        this.loadNotes();
    }

    componentDidUpdate(prevProps) {
        if (prevProps.convention?.id !== this.props.convention?.id) {
            this.loadNotes();
        }
    }

    componentWillUnmount() {
        Object.keys(this.noteTimers).forEach(key => clearTimeout(this.noteTimers[key]));
    }

    loadNotes = () => {
        const { conventionNoteStore, userStore } = this.props.stores;
        const { convention } = this.props;
        const { user } = userStore;
        if (convention?.id && user?.id) {
            conventionNoteStore.loadForConvention(convention.id);
        }
    }

    handleNoteInput = (stageMd5) => (e) => {
        const body = e.target.value;
        this.setState(prevState => ({
            noteDrafts: { ...prevState.noteDrafts, [stageMd5]: body },
        }));
        if (this.noteTimers[stageMd5]) {
            clearTimeout(this.noteTimers[stageMd5]);
        }
        this.noteTimers[stageMd5] = setTimeout(() => {
            this.saveNote(stageMd5, body);
        }, 750);
    }

    saveNote = async (stageMd5, body) => {
        const { conventionNoteStore } = this.props.stores;
        const { convention } = this.props;
        this.setState(prevState => ({
            saving: { ...prevState.saving, [stageMd5]: true },
        }));
        await conventionNoteStore.saveNote({
            convention: convention.id,
            stage: stageMd5,
            body,
        });
        this.setState(prevState => ({
            saving: { ...prevState.saving, [stageMd5]: false },
        }));
    }

    openDetail = (stageMd5) => () => {
        const { appState, userStore } = this.props.stores;
        const { user } = userStore;
        const { drawerHeightMediumLarge } = appState;
        appState.openDrawer('shooterDetail', {
            participantId: user.id,
            stageMd5,
            height: drawerHeightMediumLarge,
        });
    }

    render() {
        const { convention = {} } = this.props;
        const { userStore, conventionStore, conventionResultStore, conventionNoteStore } = this.props.stores;
        const { user = {} } = userStore;
        const { participants, classMap, disciplineMap } = conventionStore;
        const { conventionResults } = conventionResultStore;
        const { noteDrafts, saving } = this.state;

        if (!user.id || !convention.stages?.length) {
            return null;
        }

        const myStarts = [];
        convention.stages.forEach((stage, idx) => {
            const stageParticipants = convention[`participants${idx + 1}`] || [];
            const me = stageParticipants.find(p => p.id === user.id);
            if (!me) {
                return;
            }
            const detail = conventionResultStore.getShooterDetail(
                convention,
                conventionResults || [],
                participants || [],
                { participantId: user.id, stageMd5: stage.md5 },
                { classMap, disciplineMap },
            );
            myStarts.push({ stage, detail });
        });

        if (!myStarts.length) {
            return null;
        }

        return (<>
            <div class='font-weight-lighter px-3 box-header d-flex flex-row justify-content-between mb-1 mt-3'>
                <Text id='conventionNote.my-stands'>My stands</Text>
            </div>
            <div class='mx-3 mb-3'>
                <small class='text-body-secondary d-block mb-2'>
                    <i class='fa-duotone fa-lock me-1' />
                    <Text id='conventionNote.private-info'>Your analysis and notes. Only visible to you.</Text>
                </small>
                {myStarts.map(({ stage, detail }) => {
                    const note = conventionNoteStore.getNoteForStage(stage.md5);
                    const draft = noteDrafts[stage.md5] !== undefined ? noteDrafts[stage.md5] : (note?.body || '');
                    const hasResults = !!detail && detail.perStand.length > 0;
                    return (
                        <div key={stage.md5} class='card mb-3'>
                            <div class='card-body p-3'>
                                <div class='d-flex flex-row justify-content-between align-items-center mb-2'>
                                    <span style='font-weight: 500;'>
                                        {stage.name}
                                        <span class='text-body-secondary ms-2' style='font-size: 0.8rem;'>({stage.type})</span>
                                    </span>
                                    <div class='d-flex align-items-center'>
                                        {hasResults && (
                                            <span class='me-2' style='font-weight: 500;'>
                                                {detail.totalScore}
                                                {detail.totalIx > 0 && (
                                                    <span class='text-body-secondary ms-1' style='font-size: 0.8rem;'>({detail.totalIx}ix)</span>
                                                )}
                                            </span>
                                        )}
                                        {hasResults && (
                                            <a
                                                class='btn btn-sm btn-outline-secondary rounded-pill px-2 py-0 me-2'
                                                style='font-size: 0.75rem;'
                                                href={`/#/public/convention-diploma/${convention.id}/${user.id}/${stage.md5}`}
                                                target='_blank'
                                                rel='noopener noreferrer'
                                            >
                                                <i class='fa-duotone fa-award me-1' />
                                                <Text id='conventionNote.diploma'>Diplom</Text>
                                            </a>
                                        )}
                                        <button
                                            type='button'
                                            class='btn btn-sm btn-outline-primary rounded-pill px-2 py-0'
                                            style='font-size: 0.75rem;'
                                            onClick={this.openDetail(stage.md5)}
                                        >
                                            <i class='fa-duotone fa-chart-column me-1' />
                                            <Text id='conventionNote.details'>Details</Text>
                                        </button>
                                    </div>
                                </div>
                                {hasResults ? (
                                    <div class='d-flex flex-wrap mb-2'>
                                        {detail.perStand.map(perStand => (
                                            <span key={perStand.stand} class='badge text-bg-secondary me-1 mb-1' style='font-weight: 400;'>
                                                {perStand.stand}: <span style='font-weight: 600;'>{perStand.value}</span>
                                            </span>
                                        ))}
                                    </div>
                                ) : (
                                    <div class='text-body-secondary mb-2' style='font-size: 0.85rem;'>
                                        <Text id='conventionNote.no-results'>No results yet.</Text>
                                    </div>
                                )}
                                <Localizer>
                                    <textarea
                                        class='form-control mb-1'
                                        rows='2'
                                        maxLength='4000'
                                        placeholder={<Text id='conventionNote.note-placeholder'>What went well, and what went wrong?</Text>}
                                        value={draft}
                                        onInput={this.handleNoteInput(stage.md5)}
                                    />
                                </Localizer>
                                <small class='text-body-secondary'>
                                    {saving[stage.md5] ? <>
                                        <i class='fa-duotone fa-spinner fa-spin me-1' />
                                        <Text id='conventionNote.saving'>Saving...</Text>
                                    </> : <>
                                        <Text id='conventionNote.autosave-info'>Notes are private and saved automatically.</Text>
                                    </>}
                                </small>
                            </div>
                        </div>
                    );
                })}
            </div>
        </>);
    }
}

export default ConventionMyAnalysis;
